import { useEffect, useState } from "react";

import { useTranslations } from "next-intl";

import { FormButton } from "@/components/form/FormButton";
import { Icon } from "@/components/icons/Icon";
import { docPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { DocumentModel } from "@/models/DocumentModel";

const SHOW_LINK_COPIED_TIMEOUT = 3000;

export const CopyPublicLink = ({
  doc,
  className,
  disabled,
}: {
  doc: DocumentModel;
  className?: string;
  disabled?: boolean;
}) => {
  const t = useTranslations();
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (linkCopied) {
      const timeout = setTimeout(() => setLinkCopied(false), SHOW_LINK_COPIED_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [linkCopied]);

  const handleCopyClick = async () => {
    if (!doc.id) {
      return;
    }
    const documentLink = docPageUrl({ docId: doc.id }, { host: true });
    await navigator.clipboard.writeText(documentLink);
    setLinkCopied(true);
    trackEvent("share_copy_link", { doc_id: doc.id });
  };

  return (
    <FormButton
      className={twMerge("flex rounded-6 h-9 min-h-9 text-[15px]", className)}
      variant="accent"
      onClick={handleCopyClick}
      disabled={disabled}
    >
      {linkCopied ? (
        <>
          <Icon name="Check" className="-ml-1 mr-1" />
          {t("share.linkCopied")}
        </>
      ) : (
        <>
          <Icon name="Link" className="-ml-1 mr-1" />
          {t("share.copyPublicLink")}
        </>
      )}
    </FormButton>
  );
};
